import React from "react";
import type { LayoutProps } from "styled-system";
import { useModelFilterDispatch, useModelFilterSelector } from "../../../redux/store";
import {
    IconArrowRightLong,
    IconArrowLeftLong,
    Icon,
} from "../../../../../../common-deprecated/styles/v2/lexus/globals/Icon";
import * as Text from "../../../../../../common-deprecated/styles/v2/lexus/utilities/Text";
import * as Styles from "./styles/ModelResultStyles";
import { ModelFilterLabel } from "../../../utils/labels";
import type { ModelResultType } from "../../../../../../shared-logic/features/filters/utils/constants/resultConstants";
import { trackSelectResult, trackSelectModel } from "../../../utils/tracking";
import { getModelMapValueById, isRTL } from "../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { getBuildUrlBasedOnNewCarResult, getModelResultImage } from "../../../utils/helpers";
import { ImageFluid } from "../../../../../../common-deprecated/styles/v2/lexus/globals/Image";
import { redirect } from "../../../../../../common-deprecated/utils";
import ModelResultPrice from "./price/ModelResultPrice";
import ButtonLink from "../../../../../../common-deprecated/components/aem/lexus/button/ButtonLink";
import ButtonWithIcon from "../../../../../../common-deprecated/components/aem/lexus/button/ButtonWithIcon";
import { showPrice } from "../../../../../../common-deprecated/settings/utils/financeSettingUtilsCommon";
import EcoBonusPromotion from "../../../../../../common-deprecated/components/aem/lexus/EcoBonusPromotion";
import { Ellipsis } from "../../../../../../common-deprecated/styles/v2/lexus/utilities/Ellipsis";
import Spacer from "../../../../../../common-deprecated/styles/v2/lexus/utilities/Spacer";
import AvailabilityBadges from "../../../../../../common-deprecated/components/aem/lexus/availability-badges/AvailabilityBadges";
import EcoTags from "./eco-tags/EcoTags";
import EcoLabel from "../common/EcoLabel";
import type { NcPageUrlsModelDataType } from "../../../../../../common-deprecated/utils/aemPageUrlTypes";
import { CommonLabel, getCommonOptionalLabel } from "../../../../../../common-deprecated/utils/commonLabels";
import { showModal } from "../../../../../../common-deprecated/redux/actions/ModalActions";
import { MODAL_CONTENT } from "../../../../../../common-deprecated/utils/modalConstants";
import SrOnly from "../../../../../../common-deprecated/styles/v2/lexus/utilities/ScreenreaderOnly";
import useModelResultTags from "../../../hooks/useModelResultTags";

type PropsType = {
    model: ModelResultType;
    hasRightBorder?: boolean;
};

const ModelResult = ({ model, hasRightBorder }: PropsType & LayoutProps): JSX.Element => {
    const dispatch = useModelFilterDispatch();
    const commonSettings = useModelFilterSelector((state) => state.commonSettings);
    const modelFilterSettings = useModelFilterSelector((state) => state.modelFilterSettings);
    const { id, name, cars, cheapestCar = cars[0] } = model;
    const internalCode = Object.values(commonSettings.modelMap).find((item) => item.id === id)?.internalCode ?? "";

    const modelData = useModelFilterSelector((state) =>
        state.modelFilterSettings.pageUrlsModelData.find(
            (modelDataModel) => modelDataModel.modelCode.toLowerCase() === cheapestCar.model.code.toLowerCase(),
        ),
    );
    const etaTooltip = getCommonOptionalLabel({ commonSettings }, "etaTooltip");
    const etaTooltipTitle = getCommonOptionalLabel({ commonSettings }, "etaTooltipTitle");

    const {
        modelIsAvailableForOnlineSale,
        modelIsAvailableForOnlineLeasing,
        modelIsSoldOut,
        ecoTags,
        ecoBonus,
        ecoBadgeUrls,
    } = useModelResultTags({ model });

    const { isStandalone } = commonSettings;

    const { links } = getModelMapValueById(commonSettings, id) || {};

    const shouldShowPrice = showPrice(commonSettings);

    const carImage = getModelResultImage(commonSettings, model, isRTL(commonSettings) ? 31 : 4, {
        type: "png",
        width: 390,
        height: 260,
        padding: "0,0,-50,30",
        scaleMode: 3,
    });

    // URL for standalone link to build and buy
    const standaloneBuildUrl = getBuildUrlBasedOnNewCarResult(cheapestCar, commonSettings, [
        modelData ?? {},
    ] as NcPageUrlsModelDataType[]);
    const modelPageUrl = (isStandalone ? standaloneBuildUrl : links?.modelPage) || "";
    const buildPageUrl = (isStandalone ? standaloneBuildUrl : links?.buildPage) || "";
    const configureCtaUrl =
        !isStandalone && modelData?.apheleiaLiveStatusWebsite ? `${modelPageUrl}#aphgradeselector` : buildPageUrl;

    const imgAltDescr =
        cheapestCar.grade.name === "" || cheapestCar.bodyType.name === ""
            ? `${name} ${cheapestCar.grade.ecoTag ? cheapestCar.grade.ecoTag : ""} ${cheapestCar.engine.name}`
            : `${name} ${cheapestCar.grade.name} ${cheapestCar.bodyType.name}`;

    return (
        <Styles.Wrapper hasRightBorder={hasRightBorder}>
            <Styles.ImageWrapper
                href={modelPageUrl}
                onClick={(event) => {
                    event.preventDefault();
                    redirect(modelPageUrl);
                    trackSelectModel(name);
                }}
            >
                <Styles.Heading>
                    <Styles.Title>{name}</Styles.Title>
                    <EcoTags ecoTags={Array.from(ecoTags)} />

                    <Styles.EcoBadgeWrapper>
                        {ecoBadgeUrls.map((url) => url && <EcoLabel url={url} />)}
                    </Styles.EcoBadgeWrapper>
                </Styles.Heading>
                <ImageFluid src={carImage} alt={imgAltDescr} />
            </Styles.ImageWrapper>

            <Spacer mb={2} px="20px">
                <AvailabilityBadges
                    isSoldOut={modelIsSoldOut}
                    isAvailableForOnlineSales={modelIsAvailableForOnlineSale}
                    isAvailableForOnlineLeasing={modelIsAvailableForOnlineLeasing}
                />
                {shouldShowPrice && <ModelResultPrice model={model} commonSettings={commonSettings} />}
                <EcoBonusPromotion ecoBonus={ecoBonus} />
                {modelFilterSettings.showETA && model.cars[0].eta && (
                    <Styles.Eta
                        onClick={
                            etaTooltip
                                ? (event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      dispatch(
                                          showModal({
                                              type: MODAL_CONTENT,
                                              content: etaTooltip,
                                              title: etaTooltipTitle || "",
                                          }),
                                      );
                                  }
                                : undefined
                        }
                    >
                        <p>{model.cars[0].eta}</p>

                        {etaTooltip && (
                            <Styles.Eta>
                                <SrOnly>
                                    <CommonLabel label="ariaToggleEtaTooltip" />
                                </SrOnly>
                                <Styles.DisclaimerIcon>
                                    <Icon variant="info" aria-hidden="true" />
                                </Styles.DisclaimerIcon>
                            </Styles.Eta>
                        )}
                    </Styles.Eta>
                )}
            </Spacer>

            <Styles.Actions>
                <ButtonLink
                    onClick={(event) => {
                        event.preventDefault();
                        redirect(modelPageUrl);
                        trackSelectModel(name);
                    }}
                    href={modelPageUrl}
                    data-url={modelPageUrl}
                    aria-label={name}
                    data-testid={`viewmodel-${id} viewmodel-${internalCode}`}
                >
                    <ModelFilterLabel label="viewModel" />
                </ButtonLink>
                <ButtonWithIcon
                    onClick={(event) => {
                        event.preventDefault();
                        redirect(configureCtaUrl);
                        trackSelectResult(name);
                    }}
                    href={configureCtaUrl}
                >
                    <Text.IconButton>
                        <Ellipsis maxWidth="100%">
                            <ModelFilterLabel label="goToBuild" />
                        </Ellipsis>
                    </Text.IconButton>
                    {isRTL(commonSettings) ? <IconArrowLeftLong /> : <IconArrowRightLong />}
                </ButtonWithIcon>
            </Styles.Actions>
        </Styles.Wrapper>
    );
};

export default ModelResult;
